import { useState } from 'react'
import { Landmark, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PriceInput } from '@/components/ui/price-input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCurrency } from '@/lib/format'
import {
  useLoanByBoat,
  useCreateBoatLoan,
  useUpdateBoatLoan,
  useDeleteBoatLoan,
  usePayoffBoatLoan,
  usePayCurtailment,
  useUnpayCurtailment,
  usePayInterestCharge,
  useUnpayInterestCharge,
} from '@/features/inventory/hooks'
import { LoanForm, type LoanFormValues } from '@/features/inventory/loan-form'
import {
  CURTAILMENT_FREQUENCY_LABELS,
  LOAN_STATUS_LABELS,
  type Boat,
  type BoatLoan,
} from '@/types/inventory'

// "Nov 15, 2026" — due dates are date-only strings, so anchor to local
// midnight to avoid off-by-one display.
const formatDate = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

const todayISO = () => new Date().toISOString().slice(0, 10)

const isOverdue = (row: { paid_on?: string | null; due_on: string }) =>
  !row.paid_on && row.due_on.slice(0, 10) < todayISO()

// One row of the merged payment schedule: curtailments (principal) and
// interest charges (cost of money) side by side, sorted by due date with the
// sequence-matched pair adjacent.
type ScheduleRow = {
  id: string
  kind: 'curtailment' | 'interest'
  sequence: number
  due_on: string
  amount: number
  paid_on?: string | null
}

const buildScheduleRows = (loan: BoatLoan): ScheduleRow[] =>
  [
    ...loan.curtailments.map((c) => ({ ...c, kind: 'curtailment' as const })),
    ...loan.interest_charges.map((c) => ({ ...c, kind: 'interest' as const })),
  ].sort(
    (a, b) =>
      a.due_on.slice(0, 10).localeCompare(b.due_on.slice(0, 10)) ||
      a.sequence - b.sequence ||
      (a.kind === 'curtailment' ? -1 : 1),
  )

// Floor-plan slice for the boat detail page: shows the boat's active loan
// (or paid-off history), its curtailment + interest schedule with the
// mark-as-paid workflow, and the payoff action. Paid curtailments book a
// floor-plan curtailment expense and paid interest charges book a
// floor-plan interest expense in the finance ledger automatically.
export function BoatLoanCard({ boat }: { boat: Boat }) {
  const { data: loan, isLoading } = useLoanByBoat(boat.id)
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [payoffOpen, setPayoffOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)

  const createMutation = useCreateBoatLoan(boat.id)

  const handleCreate = async (values: LoanFormValues) => {
    try {
      await createMutation.mutateAsync({
        lender: values.lender,
        principal: values.principal,
        curtailment_amount: values.curtailment_amount,
        interest_rate: values.interest_rate,
        frequency: values.frequency,
        first_curtailment_on: values.first_curtailment_on,
        notes: values.notes || '',
      })
      setCreateOpen(false)
    } catch {
      // createMutation.isError surfaces the message in the dialog.
    }
  }

  // Floor plans are a new-inventory instrument: on a used boat the option
  // doesn't exist at all (used boats are consigned instead). An existing
  // loan still renders so its schedule/history stays visible.
  if (!loan && boat.condition !== 'new') return null

  if (isLoading) return <Skeleton className="h-40 w-full" />

  if (!loan) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Landmark className="h-4 w-4" /> Floor Plan
          </CardTitle>
          <CardDescription>
            Unencumbered. Add a floor-plan loan to track curtailment and interest payments.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <Button variant="outline" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> Add floor plan loan
            </Button>
            <DialogContent className="sm:max-w-xl">
              <DialogHeader>
                <DialogTitle>Finance {boat.make} {boat.model}</DialogTitle>
                <DialogDescription>
                  Record the lender's terms; the curtailment and interest schedules generate
                  automatically.
                </DialogDescription>
              </DialogHeader>
              <LoanForm
                onSubmit={handleCreate}
                isPending={createMutation.isPending}
                submitLabel="Create Loan"
              />
              {createMutation.isError && (
                <p className="text-sm text-destructive">
                  Failed to create loan: {createMutation.error.message}
                </p>
              )}
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    )
  }

  return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <Landmark className="h-4 w-4" /> Floor Plan
            <Badge variant={loan.status === 'active' ? 'default' : 'secondary'}>
              {LOAN_STATUS_LABELS[loan.status]}
            </Badge>
          </CardTitle>
          <CardDescription>{loan.lender}</CardDescription>
        </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Principal financed</span>
            <span>{formatCurrency(loan.principal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Curtailment</span>
            <span>
              {formatCurrency(loan.curtailment_amount)} ·{' '}
              {CURTAILMENT_FREQUENCY_LABELS[loan.frequency].toLowerCase()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">First curtailment</span>
            <span>{formatDate(loan.first_curtailment_on)}</span>
          </div>
          {loan.interest_rate > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Interest rate</span>
              <span>{loan.interest_rate}% APR</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Curtailments paid</span>
            <span>{formatCurrency(loan.paid_total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Interest paid</span>
            <span>{formatCurrency(loan.interest_paid_total)}</span>
          </div>
          <div className="flex justify-between border-t pt-1 font-medium">
            <span className="text-muted-foreground">Total financing cost</span>
            <span>{formatCurrency(loan.paid_total + loan.interest_paid_total)}</span>
          </div>
          {loan.status === 'active' ? (
            <>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Accrued unpaid</span>
                <span className={loan.accrued_unpaid_count > 0 ? 'font-medium text-destructive' : ''}>
                  {loan.accrued_unpaid_count > 0
                    ? `${formatCurrency(loan.accrued_unpaid_total)} (${loan.accrued_unpaid_count} due)`
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next due</span>
                <span>{loan.next_due_on ? formatDate(loan.next_due_on) : '—'}</span>
              </div>
            </>
          ) : (
            <>
              {loan.paid_off_on && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Paid off</span>
                  <span>{formatDate(loan.paid_off_on)}</span>
                </div>
              )}
              {loan.payoff_amount != null && loan.payoff_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payoff amount</span>
                  <span>{formatCurrency(loan.payoff_amount)}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSchedule((v) => !v)}>
            {showSchedule ? 'Hide schedule' : 'View schedule'}
          </Button>
          {loan.status === 'active' && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit terms
              </Button>
              <PayoffDialog loan={loan} open={payoffOpen} onOpenChange={setPayoffOpen} />
            </>
          )}
          <DeleteLoanDialog loan={loan} open={deleteOpen} onOpenChange={setDeleteOpen} />
        </div>

        {showSchedule && <PaymentSchedule loan={loan} />}

        <EditLoanDialog loan={loan} open={editOpen} onOpenChange={setEditOpen} />
      </CardContent>
    </Card>
  )
}

function EditLoanDialog({
  loan,
  open,
  onOpenChange,
}: {
  loan: BoatLoan
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const updateMutation = useUpdateBoatLoan(loan.boat_id)

  const handleSubmit = async (values: LoanFormValues) => {
    try {
      await updateMutation.mutateAsync({
        loanId: loan.id,
        body: {
          lender: values.lender,
          principal: values.principal,
          curtailment_amount: values.curtailment_amount,
          interest_rate: values.interest_rate,
          frequency: values.frequency,
          first_curtailment_on: values.first_curtailment_on,
          notes: values.notes || '',
        },
      })
      onOpenChange(false)
    } catch {
      // isError surfaces the message in the dialog.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit Floor Plan Terms</DialogTitle>
          <DialogDescription>
            Unpaid curtailments and interest charges regenerate from the new terms; paid history is
            untouched.
          </DialogDescription>
        </DialogHeader>
        <LoanForm
          loan={loan}
          onSubmit={handleSubmit}
          isPending={updateMutation.isPending}
          submitLabel="Save Terms"
        />
        {updateMutation.isError && (
          <p className="text-sm text-destructive">
            Failed to update loan: {updateMutation.error.message}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PayoffDialog({
  loan,
  open,
  onOpenChange,
}: {
  loan: BoatLoan
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const payoffMutation = usePayoffBoatLoan(loan.boat_id)
  const [paidOffOn, setPaidOffOn] = useState(todayISO())
  const [payoffAmount, setPayoffAmount] = useState('')

  const handlePayoff = async () => {
    try {
      await payoffMutation.mutateAsync({
        loanId: loan.id,
        body: {
          paid_off_on: paidOffOn || undefined,
          payoff_amount: payoffAmount ? Number(payoffAmount.replace(/,/g, '')) : undefined,
        },
      })
      onOpenChange(false)
    } catch {
      // isError surfaces the message in the dialog.
    }
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => onOpenChange(true)}>
        Record payoff
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pay off {loan.lender} loan?</DialogTitle>
            <DialogDescription>
              Unpaid curtailments and interest charges after the payoff date are settled by the
              payoff and removed. The payoff itself books no ledger entry — it settles through the
              boat sale.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="loan-paid-off-on">Payoff date</Label>
              <Input
                id="loan-paid-off-on"
                type="date"
                value={paidOffOn}
                onChange={(e) => setPaidOffOn(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="loan-payoff-amount">Payoff amount (optional)</Label>
              <PriceInput
                value={payoffAmount}
                onChange={setPayoffAmount}
                onBlur={() => {}}
                placeholder="142,500"
              />
              <p className="text-xs text-muted-foreground">
                The lender's quoted payoff, recorded for reference.
              </p>
            </div>
          </div>
          {payoffMutation.isError && (
            <p className="text-sm text-destructive">
              Failed to record payoff: {payoffMutation.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handlePayoff} disabled={payoffMutation.isPending}>
              {payoffMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record payoff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function DeleteLoanDialog({
  loan,
  open,
  onOpenChange,
}: {
  loan: BoatLoan
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const deleteMutation = useDeleteBoatLoan(loan.boat_id)

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(loan.id)
      onOpenChange(false)
    } catch {
      // isError surfaces the message in the dialog.
    }
  }

  return (
    <>
      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onOpenChange(true)}>
        <Trash2 className="mr-2 h-4 w-4" /> Delete
      </Button>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete floor plan loan?</DialogTitle>
            <DialogDescription>
              This permanently deletes the {loan.lender} loan, both payment schedules and the
              ledger entries for paid curtailments and interest. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteMutation.isError && (
            <p className="text-sm text-destructive">
              Failed to delete loan: {deleteMutation.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function PaymentSchedule({ loan }: { loan: BoatLoan }) {
  const payCurtailment = usePayCurtailment(loan.boat_id)
  const unpayCurtailment = useUnpayCurtailment(loan.boat_id)
  const payInterest = usePayInterestCharge(loan.boat_id)
  const unpayInterest = useUnpayInterestCharge(loan.boat_id)
  const busy =
    payCurtailment.isPending ||
    unpayCurtailment.isPending ||
    payInterest.isPending ||
    unpayInterest.isPending

  const error =
    payCurtailment.error ??
    unpayCurtailment.error ??
    payInterest.error ??
    unpayInterest.error

  const rows = buildScheduleRows(loan)

  const markPaid = (row: ScheduleRow) =>
    row.kind === 'curtailment'
      ? payCurtailment.mutate({ loanId: loan.id, curtailmentId: row.id })
      : payInterest.mutate({ loanId: loan.id, chargeId: row.id })

  const markUnpaid = (row: ScheduleRow) =>
    row.kind === 'curtailment'
      ? unpayCurtailment.mutate({ loanId: loan.id, curtailmentId: row.id })
      : unpayInterest.mutate({ loanId: loan.id, chargeId: row.id })

  const pendingId = (row: ScheduleRow) =>
    (payCurtailment.variables?.curtailmentId === row.id && payCurtailment.isPending) ||
    (unpayCurtailment.variables?.curtailmentId === row.id && unpayCurtailment.isPending) ||
    (payInterest.variables?.chargeId === row.id && payInterest.isPending) ||
    (unpayInterest.variables?.chargeId === row.id && unpayInterest.isPending)

  return (
    <div className="space-y-2">
      {error && (
        <p className="text-sm text-destructive">Failed to update payment: {error.message}</p>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8 px-2">#</TableHead>
              <TableHead className="px-2">Type</TableHead>
              <TableHead className="px-2">Due</TableHead>
              <TableHead className="px-2 text-right">Amount</TableHead>
              <TableHead className="px-2">Status</TableHead>
              <TableHead className="w-16 px-2" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} className="text-xs">
                <TableCell className="px-2 text-muted-foreground">{row.sequence}</TableCell>
                <TableCell className="px-2">
                  <Badge
                    variant="outline"
                    className={
                      row.kind === 'curtailment'
                        ? 'border-primary/30 text-primary'
                        : 'border-amber-500/40 text-amber-600'
                    }
                  >
                    {row.kind === 'curtailment' ? 'Curtailment' : 'Interest'}
                  </Badge>
                </TableCell>
                <TableCell className="px-2">{formatDate(row.due_on)}</TableCell>
                <TableCell className="px-2 text-right">{formatCurrency(row.amount)}</TableCell>
                <TableCell className="px-2">
                  {row.paid_on ? (
                    <Badge
                      variant="outline"
                      className="border-success/30 bg-success/15 text-success"
                      title={`Paid ${formatDate(row.paid_on)}`}
                    >
                      Paid
                    </Badge>
                  ) : isOverdue(row) ? (
                    <Badge variant="destructive">Overdue</Badge>
                  ) : (
                    <Badge variant="secondary">Due</Badge>
                  )}
                </TableCell>
                <TableCell className="px-2">
                  {row.paid_on ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={busy}
                      onClick={() => markUnpaid(row)}
                    >
                      Unpay
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={busy}
                      onClick={() => markPaid(row)}
                    >
                      {pendingId(row) ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pay'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No payments scheduled yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        Marking a curtailment books a floor-plan curtailment expense; marking an interest charge
        books a floor-plan interest expense — both on the paid date. Unpaying removes the entry.
        Rows generate as periods come due.
      </p>
    </div>
  )
}
